import XDate from 'xdate';
import {eventApi} from '~/CreateFabricInvitation/api/endpoints/event';
import {
  selectSelectedBackground,
  selectSelectedEnvelope,
  selectSelectedLiner,
  selectSelectedOverlay,
  selectSelectedReveal,
  selectSelectedStamp,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {updateEventIdInPathname} from '~/CreateFabricInvitation/utils/save';
import {SET_EVITE_EVENT} from '~/common/fabric/reducers/eviteEvent/constants';
import {selectHostIds, selectPendingHostIds} from '~/common/fabric/reducers/eviteEvent/selectors';
import {mapGuestOptionsToBackend} from '~/common/fabric/reducers/guestOptions/fieldMapping';
import {SET_WIDGETS} from '~/common/fabric/reducers/widgets/constants';
import {GCS_PATH_IDS, gcsUploadImage} from '~/common/storage/gcs_storage';
import {formatTimezoneOffset, removeNestedProperty} from '~/common/utils';
import {dataURLToFile} from '~/common/utils/dataURLToFile';
import {fetchInvitationSummary} from '~/ViewInvitation/reducers/apiActions';

// TODO: we are commenting this out for a temporary hot fix (EVT-27156). Uncomment when EVT-27159 is fixed
// import isEqual from 'lodash/isEqual';
// import {setEventToBackendSnapshot} from '~/common/fabric/Snapshot/reducers';
// import {selectEventToBackendSnapshot} from '~/common/fabric/Snapshot/reducers/selector';

const twoWeeksFromNow = () => {
  const future = new XDate().addWeeks(2).setHours(18).setMinutes(0).setSeconds(0);
  return future.toString('i').slice(0, 16) + formatTimezoneOffset();
};

async function uploadUyoStickersIfNeededAndReturnFabricStickers(envelopeStickersObjects, eventId) {
  return Promise.all(
    envelopeStickersObjects.map(async (obj) => {
      let url = obj.src || obj.originalUrl || '';
      if (url.startsWith('data:')) {
        const gcsUploadArgs = {
          gcsPathId: GCS_PATH_IDS.anonymous_image,
          params: {
            // biome-ignore lint/style/useNamingConvention: existing error
            event_id: eventId,
            // biome-ignore lint/style/useNamingConvention: existing error
            image_id: '',
          },
          image: dataURLToFile(url, 'uyo_sticker'),
        };
        url = (await gcsUploadImage(gcsUploadArgs))?.url;
      }
      return {...obj, originalUrl: url, src: url};
    })
  );
}

export const stateToBackendEventRepresentation = async (state) => {
  const {
    details,
    giftingOptions,
    fabricTemplate,
    eviteEvent,
    fabricDesign,
    coverData,
    guestOptions,
  } = state;
  const background = selectSelectedBackground(state);
  const overlay = selectSelectedOverlay(state);
  const reveal = selectSelectedReveal(state);
  const stamp = selectSelectedStamp(state);
  const liner = selectSelectedLiner(state);
  const envelope = selectSelectedEnvelope(state);
  const {widgets, eventAttributes} = state;
  const {
    gift_registry_name: giftRegistryName,
    gift_registry_note: giftRegistryNote,
    gift_registry_urls: giftRegistryUrls,
    gift_registry_type: giftRegistryType,
    pledgeling_data: pledgelingData,
  } = giftingOptions;

  const hostIDs = selectHostIds(state);
  const pendingHostIDs = selectPendingHostIds(state);

  const eventTypeSource = window.dataLayer?.find(
    (obj) => 'event_type_source' in obj
  )?.event_type_source;

  const event = {
    ...eviteEvent,
    // biome-ignore lint/style/useNamingConvention: existing error
    host_ids: hostIDs,
    // biome-ignore lint/style/useNamingConvention: existing error
    pending_host_ids: pendingHostIDs,
    origin: 21,
    status: eviteEvent.status || 'draft',
    // biome-ignore lint/style/useNamingConvention: existing error
    template_name: fabricTemplate.templateName,
    // biome-ignore lint/style/useNamingConvention: existing error
    fabric_design_data: fabricDesign.fabricArt,
    // biome-ignore lint/style/useNamingConvention: existing error
    host_name: details.host,
    // biome-ignore lint/style/useNamingConvention: existing error
    phone_number: details.phoneNumber,
    // biome-ignore lint/style/useNamingConvention: existing error
    event_type: details.type,
    // biome-ignore lint/style/useNamingConvention: existing error
    cohost_name1: details.cohost.name,
    // biome-ignore lint/style/useNamingConvention: existing error
    cohost_email1: details.cohost.email,
    // biome-ignore lint/style/useNamingConvention: existing error
    guests_of_honor: {...details.guestsOfHonor},
    // biome-ignore lint/style/useNamingConvention: existing error
    location_name: details.location,
    // biome-ignore lint/style/useNamingConvention: existing error
    place_id: details.placeId,
    // biome-ignore lint/style/useNamingConvention: existing error
    meeting_url: details.meetingUrl,
    // biome-ignore lint/style/useNamingConvention: existing error
    street_address: details.address.street,
    // biome-ignore lint/style/useNamingConvention: existing error
    unit_num: details.address.unitNum,
    city: details.address.city,
    state: details.address.state,
    // biome-ignore lint/style/useNamingConvention: existing error
    zip_code: details.address.zip,
    country: details.address.country,
    title: details.title,
    message: details.notes,
    // biome-ignore lint/style/useNamingConvention: existing error
    start_datetime: details.start
      ? XDate(details.start).toString('i').slice(0, 16) + formatTimezoneOffset()
      : twoWeeksFromNow(),
    // biome-ignore lint/style/useNamingConvention: existing error
    end_datetime:
      details.end && XDate(details.end).toString('i').slice(0, 16) + formatTimezoneOffset(),
    // biome-ignore lint/style/useNamingConvention: existing error
    stop_datetime:
      details.stop && XDate(details.stop).toString('i').slice(0, 16) + formatTimezoneOffset(),
    // biome-ignore lint/style/useNamingConvention: existing error
    known_timezone_name: details.known_timezone_name || coverData.known_timezone_name,
    // biome-ignore lint/style/useNamingConvention: existing error
    known_timezone_abbr: details.known_timezone_abbr || coverData.known_timezone_abbr,
    // send_on: ,
    // biome-ignore lint/style/useNamingConvention: existing error
    gift_registry_urls: giftRegistryUrls,
    // biome-ignore lint/style/useNamingConvention: existing error
    gift_registry_name: giftRegistryName,
    // biome-ignore lint/style/useNamingConvention: existing error
    gift_registry_note: giftRegistryNote,
    // biome-ignore lint/style/useNamingConvention: existing error
    gift_registry_type: giftRegistryType,
    // biome-ignore lint/style/useNamingConvention: existing error
    rsvp_off: details.rsvp_off,
    // biome-ignore lint/style/useNamingConvention: existing error
    gift_keywords: null,
    // biome-ignore lint/style/useNamingConvention: existing error
    gift_list_url: null,
    wishlist: null,
    options: {
      ...eviteEvent.options,
      ...giftingOptions.options,
      ...mapGuestOptionsToBackend(guestOptions),
      // biome-ignore lint/style/useNamingConvention: existing error
      event_option_allow_view_map: details.showMap,
      // biome-ignore lint/style/useNamingConvention: existing error
      event_option_allow_invite_more: false, // This will no longer be supported in Fabric invitations
      // Remove rendered data so we don't overwrite what is on the server getting set asynchronously
      // biome-ignore lint/style/useNamingConvention: existing error
      rendered_image_filename: undefined,
      // biome-ignore lint/style/useNamingConvention: existing error
      rendered_image_gs_key: undefined,
      // biome-ignore lint/style/useNamingConvention: existing error
      rendered_image_url: undefined,
      // biome-ignore lint/style/useNamingConvention: existing error
      rendered_text_image_url: undefined,
    },
    // biome-ignore lint/style/useNamingConvention: existing error
    is_fabric_premium: details.isPremium,
    // biome-ignore lint/style/useNamingConvention: existing error
    pledgeling_data: pledgelingData,
    // biome-ignore lint/style/useNamingConvention: existing error
    pledgeling_new_api: true,
    // fields to not send back
    // biome-ignore lint/style/useNamingConvention: existing error
    guessing_games_data: undefined,
    // biome-ignore lint/style/useNamingConvention: existing error
    sent_on: undefined,
    // biome-ignore lint/style/useNamingConvention: existing error
    last_updated: undefined,
    // biome-ignore lint/style/useNamingConvention: existing error
    date_created: undefined,
    // biome-ignore lint/style/useNamingConvention: existing error
    event_type_source: eventTypeSource,
  };

  event.options.design = {...(event.options.design || {})};
  event.used_as = eviteEvent.id ? event.used_as : fabricTemplate.usedAs;
  const {design} = event.options;
  if (envelope) {
    design.envelope = envelope.id;
    design.envelope_flap_down = envelope.flap_down_image;
    design.envelope_flap_up = envelope.blank;
    design.envelope_front = envelope.image;
    design.name_style = {
      // Text style for names on front of envelopes
      align: 'center',
      color: envelope.text_color,
      font: 'Financier',
      position: {x: 15, x2: 605, y: 225, y2: 465},
      size: 48,
    };

    if (liner) {
      design.liner = liner.id;
      liner.media.forEach((media) => {
        if (media.label === 'image') design.liner_image = media.url;
      });
    }
    if (stamp) {
      design.stamp = stamp.id;
      stamp.media.forEach((media) => {
        if (media.label === 'image') design.stamp_image = media.url;
      });
    }
    if (background) {
      design.background = background.id;
      background.media.forEach((media) => {
        if (media.label === 'mobile') design.background_image_mobile = media.url;
        else if (media.label === 'desktop') design.background_image_desktop = media.url;
      });
      design.background_color = null;
    } else {
      design.background = null;
      design.background_image_mobile = null;
      design.background_image_desktop = null;
      design.background_color = fabricDesign.backgroundColor;
    }
  }
  if (overlay) {
    design.overlay_id = overlay.id;
    overlay.media.forEach((media) => {
      if (media.label === 'mobile_vp9') design.overlay_video_mobile_vp9 = media.url;
      else if (media.label === 'desktop_vp9') design.overlay_video_desktop_vp9 = media.url;
      else if (media.label === 'mobile_h265') design.overlay_video_mobile_h265 = media.url;
      else if (media.label === 'desktop_h265') design.overlay_video_desktop_h265 = media.url;
    });
  } else {
    design.overlay_id = '';
    design.overlay_video_mobile_vp9 = '';
    design.overlay_video_desktop_vp9 = '';
    design.overlay_video_mobile_h265 = '';
    design.overlay_video_desktop_h265 = '';
  }
  if (reveal) {
    design.reveal_id = reveal.id;
    reveal.media.forEach((media) => {
      if (media.label === 'mobile_vp9') design.reveal_video_mobile_vp9 = media.url;
      else if (media.label === 'desktop_vp9') design.reveal_video_desktop_vp9 = media.url;
      else if (media.label === 'mobile_h265') design.reveal_video_mobile_h265 = media.url;
      else if (media.label === 'desktop_h265') design.reveal_video_desktop_h265 = media.url;
    });
  } else {
    design.reveal_id = '';
    design.reveal_video_mobile_vp9 = '';
    design.reveal_video_desktop_vp9 = '';
    design.reveal_video_mobile_h265 = '';
    design.reveal_video_desktop_h265 = '';
  }

  if (fabricDesign.envelopeStickers)
    // duplicate the envelope stickers object to avoid mutating the original state
    design.envelope_fabric_stickers = JSON.parse(JSON.stringify(fabricDesign.envelopeStickers));
  else design.envelope_fabric_stickers = fabricDesign.envelopeStickers;

  if (design.envelope_fabric_stickers?.objects?.length > 0) {
    design.envelope_fabric_stickers.objects =
      await uploadUyoStickersIfNeededAndReturnFabricStickers(
        design.envelope_fabric_stickers.objects,
        event.id
      );
  }
  design.card_shadow = fabricTemplate.cardShadow;
  // We don't need to send "created" fields to the BE since it does not make sense to update them
  removeNestedProperty(widgets, 'created');

  if (!event.tracking_data)
    // biome-ignore lint/style/useNamingConvention: existing error
    event.tracking_data = {gallery_category: details.tracking_gallery_category};

  window.saveUTMToObj(event);

  return {event, widgets, attributes: eventAttributes};
};

export const getSaveEventURL = (eventId) =>
  eventId
    ? new URL(`/ajax/event/${eventId}/update/`, window.location.origin)
    : new URL('/ajax/event/create/', window.location.origin);

export const createOrUpdateEvent = async (url, body) =>
  evite.fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-CSRFToken': evite.cookie('csrftoken'),
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

export const saveEventThunk =
  (saveToBackend = true, delayFetchSummaryTime = 0) =>
  async (dispatch, getState) => {
    const state = getState();
    const {
      eviteEvent,
      details: {inEditMode, guestsOfHonor},
    } = state;
    const eventId = eviteEvent.id;
    const url = getSaveEventURL(eventId);
    const eventRepresentation = await stateToBackendEventRepresentation(state);
    let saveStatus;

    if (saveToBackend) {
      let resp;
      try {
        resp = await createOrUpdateEvent(url, eventRepresentation);

        saveStatus = resp.status;
        // hack for sending the event after modifying guests of honor
        if (inEditMode && guestsOfHonor?.update) {
          await eventApi.endpoints.send.initiate(state.eviteEvent.id);
        }
        if (!resp?.ok) {
          const {message} = await resp.json();
          throw new Error(message);
        }
      } catch (e) {
        if (e.message === 'Failed to fetch') {
          throw new Error('Unable to save event, please check your internet connection');
        }
        throw e;
      }

      const results = await resp.json();

      if (!eventId) updateEventIdInPathname(results.event.id);
      window.evite_event = results.event;
      dispatch({type: SET_EVITE_EVENT, payload: results.event});
      dispatch({type: SET_WIDGETS, payload: results});
      // TODO: we are commenting this out for a temporary hot fix (EVT-27156). Uncomment when EVT-27159 is fixed
      // dispatch(
      //     setEventToBackendSnapshot(stateToBackendEventRepresentation(getState(), sendInvitations))
      // );
    } else {
      window.evite_event = eventRepresentation.event;
      dispatch({type: SET_EVITE_EVENT, payload: eventRepresentation.event});
      // TODO: we are commenting this out for a temporary hot fix (EVT-27156). Uncomment when EVT-27159 is fixed
      // dispatch(
      //     setEventToBackendSnapshot(stateToBackendEventRepresentation(getState(), sendInvitations))
      // );
    }

    if (window.evite_event) {
      if (delayFetchSummaryTime > 0) {
        await new Promise((resolve) => {
          setTimeout(resolve, delayFetchSummaryTime);
        });
      }
      await dispatch(fetchInvitationSummary(window.evite_event.id));
    }
    return saveStatus;
  };
